// pages/ServicesPageFennoFuture.tsx - FennoFuture services (cleaned up)
import React from 'react';
import { Cpu, Settings, Wrench, CheckCircle, FileText, Target, Lightbulb, Shield, Users, Anchor, ArrowRight } from 'lucide-react';

const ServicesPageFennoFuture: React.FC = () => {
  const values = [
    {
      icon: <Lightbulb className="w-8 h-8" />,
      title: 'Innovaatio',
      description: 'Hyödynnämme uusinta teknologiaa ja menetelmiä asiakkaidemme hyväksi.'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Luotettavuus',
      description: 'Sitoudumme sovittuihin aikatauluihin ja lupaamme vain sen mitä voimme toteuttaa.'
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: 'Laatu',
      description: 'Jokainen projekti toteutetaan korkealla ammattitaidolla ja huolellisesti.'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Asiakaslähtöisyys',
      description: 'Kuuntelemme asiakkaitamme ja räätälöimme ratkaisut heidän tarpeidensa mukaan.'
    }
  ];

  const services = [
    {
      icon: <Cpu className="w-8 h-8" />,
      title: 'Tuotteiden suunnittelu',
      description: 'CAD-suunnittelu ja 3D-mallinnus. Prototyyppien valmistus ja tuotekehitys.',
      features: [
        '3D CAD-suunnittelu',
        'Prototyypit',
        'Muovitulostus',
        'Silikonivalu',
        'Materiaalioptimointi',
        'Tekninen dokumentaatio'
      ],
      color: 'from-blue-600 to-purple-600'
    },
    {
      icon: <Settings className="w-8 h-8" />,
      title: 'Valmistus & Tuotanto',
      description: 'Metallituotteiden valmistus moderneilla menetelmillä. Pienistä sarjoista suuriin tuotantoeriin.',
      features: [
        'Sorvaus ja jyrsintä',
        'Hitsaus',
        'Pintakäsittely',
        'Puhdistus',
        'Mittaus ja laadunvalvonta',
        'Kokoonpano'
      ],
      color: 'from-purple-600 to-pink-600'
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: 'Digitalisointi & Piirustukset',
      description: 'Vanhojen piirustusten ja PDF-dokumenttien modernisointi digitaaliseen muotoon.',
      features: [
        'PDF-piirustusten uudelleenpiirto',
        'Vanhojen piirustusten digitointi',
        'CAD-formaattiin muunto',
        'Piirustusten päivitys ja korjaus',
        'Teknisen dokumentaation modernisointi',
        'Vektorimuotoinen lopputulos'
      ],
      color: 'from-green-600 to-teal-600'
    },
    {
      icon: <Wrench className="w-8 h-8" />,
      title: 'Korjaus & Huolto',
      description: 'Luotettavaa vikadiagnostiikkaa sekä korjaus- ja huoltopalvelua teknisille tuotteille.',
      features: [
        'Vikadiagnostiikka',
        'Korjauspalvelu',
        'Määräaikaishuollot',
        'Varaosapalvelu',
        'Testaus ja käyttöönotto',
        'Raportointi'
      ],
      color: 'from-cyan-600 to-blue-600'
    }
  ];

  return (
    <div className="pt-20 min-h-screen bg-gray-50">
      {/* Hero - matching homepage style */}
      <div className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 py-20 overflow-hidden">
        {/* Background elements */}
        <div className="absolute inset-0">
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `linear-gradient(to right, rgba(59, 130, 246, 0.1) 1px, transparent 1px),
                               linear-gradient(to bottom, rgba(59, 130, 246, 0.1) 1px, transparent 1px)`,
              backgroundSize: '50px 50px'
            }}
          />
          <div className="absolute top-10 right-10 w-64 h-64 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full blur-3xl opacity-20" />
          <div className="absolute bottom-10 left-10 w-48 h-48 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full blur-3xl opacity-20" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Palvelumme
          </h1>
        </div>

        {/* Smooth transition to white */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white via-white/50 to-transparent pointer-events-none" />
      </div>

      {/* Mission & Tagline */}
      <div className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-2xl md:text-3xl font-semibold text-gray-900 mb-6">
            FennoFuture - Ideasta toimivaan ratkaisuun suunnittelusta toteutukseen.
          </p>
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Missiomme</h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Tarjoamme laadukkaita tuotteiden suunnittelu- ja valmistuspalveluita, jotka 
            perustuvat pitkäaikaiseen tekniseen kokemukseen. Autamme asiakkaitamme 
            muuttamaan ideat toimiviksi ratkaisuiksi - suunnittelusta valmiiseen tuotteeseen.
          </p>
        </div>
      </div>

      {/* Values */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">Arvomme</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center text-white mb-4">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold mb-2">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Services Grid */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div 
                key={index} 
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 group"
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-2xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform`}>
                  {service.icon}
                </div>
                <h3 className="text-2xl font-bold mb-4 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-pink-600 transition-all">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Partner Service - Venepalvelut */}
      <div className="py-20 bg-gradient-to-br from-cyan-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold">Venepalvelut</h2>
          </div>

          <a
            href="https://potkurikorjaus.fi"
            target="_blank"
            rel="noopener noreferrer"
            className="block bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group"
          >
            <div className="flex flex-col lg:flex-row">
              {/* Left side - Icon & branding */}
              <div className="lg:w-1/3 bg-gradient-to-br from-cyan-500 to-teal-500 p-8 flex flex-col items-center justify-center text-white">
                <Anchor className="w-16 h-16 mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-2xl font-bold mb-2">Potkurikorjaus.fi</h3>
                <p className="text-cyan-100 text-center">Pirkanmaan Potkurikorjaus</p>
                <p className="text-cyan-200 text-sm mt-1">By FennoFuture</p>
              </div>

              {/* Right side - Content */}
              <div className="lg:w-2/3 p-8">
                <p className="text-gray-600 mb-6 text-lg">
                  Ammattitaitoista venepotkurien korjausta Pirkanmaalla.
                  Korjaamme alumiini-, teräs- ja pronssipotkurit. 12 kuukauden takuu kaikille töille.
                </p>

                <div className="grid sm:grid-cols-2 gap-3 mb-6">
                  {['Potkurien oikaisu ja korjaus', 'Tasapainotus', 'Kiillotus ja pinnoitus',
                    'Nouto- ja toimituspalvelu', '12 kuukauden takuu', 'Varaosat'].map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-cyan-500 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="flex items-center gap-2 text-cyan-600 font-semibold group-hover:gap-3 transition-all">
                  <span>Lisätietoa täältä</span>
                  <ArrowRight className="w-5 h-5" />
                </div>
              </div>
            </div>
          </a>
        </div>
      </div>

      {/* CTA */}
      <div className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Tarvitsetko apua projektissasi?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Ota yhteyttä, niin keskustellaan miten voimme auttaa
          </p>
          <a 
            href="/yhteystiedot"
            className="inline-block bg-white text-blue-600 px-8 py-4 rounded-full font-semibold hover:shadow-xl transition-all"
          >
            Ota yhteyttä
          </a>
        </div>
      </div>
    </div>
  );
};

export default ServicesPageFennoFuture;
